import React, { createContext, useContext, useState, useEffect, ReactNode, useRef } from 'react';
import { MemeTemplate } from '../types';
import { useMemeApi } from '../api/memeApi';

interface MemeTemplateContextType {
    templates: MemeTemplate[];
    loading: boolean;
    error: string | null;
    searchTerm: string;
    setSearchTerm: (term: string) => void;
    filteredTemplates: MemeTemplate[];
}

const MemeTemplateContext = createContext<MemeTemplateContextType | undefined>(undefined);

export const useMemeTemplates = () => {
    const context = useContext(MemeTemplateContext);
    if (context === undefined) {
        throw new Error('useMemeTemplates must be used within a MemeTemplateProvider');
    }
    return context;
};

interface MemeTemplateProviderProps {
    children: ReactNode;
}

export const MemeTemplateProvider: React.FC<MemeTemplateProviderProps> = ({ children }) => {
    const [templates, setTemplates] = useState<MemeTemplate[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [searchTerm, setSearchTerm] = useState('');

    // Use our meme API hook
    const { fetchMemeTemplates } = useMemeApi();

    // Use a ref to track if we've already loaded templates
    const loadedRef = useRef(false);

    useEffect(() => {
        // Only load templates once
        if (loadedRef.current) return;

        const loadTemplates = async () => {
            try {
                setLoading(true);
                const data = await fetchMemeTemplates();
                setTemplates(data);
                setError(null);
                // Mark as loaded to prevent future calls
                loadedRef.current = true;
            } catch (err) {
                setError('Failed to load meme templates');
                console.error(err);
            } finally {
                setLoading(false);
            }
        };

        loadTemplates();
    }, [fetchMemeTemplates]); // We still depend on fetchMemeTemplates, but now it's memoized

    // Filter templates based on search term
    const filteredTemplates = templates.filter((template) => {
        if (!searchTerm.trim()) return true; // Return all templates if search is empty

        const term = searchTerm.toLowerCase();
        return (
            template.name.toLowerCase().includes(term) ||
            template.tags.some((tag) => tag.toLowerCase().includes(term))
        );
    });

    const value = {
        templates,
        loading,
        error,
        searchTerm,
        setSearchTerm,
        filteredTemplates,
    };

    return <MemeTemplateContext.Provider value={value}>{children}</MemeTemplateContext.Provider>;
};